import { DirectUpload } from "@rails/activestorage"
import { CheckCircle2, Loader2, X } from "lucide-react"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/utils"

/**
 * OKS-55 — Direct-upload video field for the masterclass module form.
 *
 * Wraps `@rails/activestorage` `DirectUpload` (in-deps, precedent:
 * `mission-attachments-dropzone.tsx`). Streams the chosen file to
 * S3 via Rails' default `/rails/active_storage/direct_uploads` endpoint
 * and emits a hidden `<input name={fieldName} value={signedId}>` so the
 * parent Inertia `<Form>` submits the signed_id to the controller's
 * `:video` strong-param.
 *
 * **Crew hard rule:** uses the global `<Form>` from Inertia (the wrapper
 * around this field), NOT the shadcn `<Form>` / react-hook-form / zod.
 * This component only emits a hidden input and exposes upload status
 * via callbacks — the parent owns submit gating.
 *
 * UX Spec §2 / §4:
 *   - Choose video file → Progress bar (0–100) → "Ready to save" check
 *   - Cancel button aborts the in-flight DirectUpload (xhr.abort)
 *   - On upload failure: "Upload failed — try again" inline error
 *
 * Submit gating: `onStatusChange("uploading" | "ready" | "idle" |
 * "error")` lets the parent form disable submit while uploading.
 */
const DEFAULT_DIRECT_UPLOAD_URL = "/rails/active_storage/direct_uploads"

// OKS-82 MIME allowlist (model-layer validator).
const ACCEPTED_MIME = "video/mp4,video/quicktime,video/webm"
const MAX_BYTES = 200 * 1024 * 1024 // 200 MB soft cap (FE only — server validates blob_size separately)

export type UploadStatus = "idle" | "uploading" | "ready" | "error"

export interface MasterclassModuleUploadFieldProps {
  /** `name` attribute on the hidden signed_id input — wired into Inertia Form params. */
  fieldName?: string
  /** Direct-upload endpoint (Rails default). Overridable for testing. */
  directUploadUrl?: string
  /** Initial filename + signedId if the user is re-editing an existing module. */
  initialFilename?: string | null
  /** Submit-gate callback so the parent disables Save while uploading. */
  onStatusChange?: (status: UploadStatus) => void
  /** Optional `data-testid` namespace for the field wrapper. */
  testId?: string
  /** Initial signed_id if re-editing (lets the user keep the existing blob). */
  initialSignedId?: string | null
  /** Inline error message from the parent `errors.video` Inertia prop.
   *  Inertia configures `errorValueType: string[]` in `globals.d.ts` —
   *  React renders an array of strings as joined siblings, but accepting
   *  `string | string[] | null` keeps the type contract honest. */
  errorMessage?: string | string[] | null
  /** Disable the picker (e.g. when soft-cap is reached). */
  disabled?: boolean
}

interface FileEntry {
  filename: string
  byteSize: number
  contentType: string
  status: UploadStatus
  signedId: string | null
  progress: number
  error: string | null
}

export function MasterclassModuleUploadField({
  fieldName = "masterclass_module[video]",
  directUploadUrl = DEFAULT_DIRECT_UPLOAD_URL,
  initialFilename = null,
  onStatusChange,
  testId = "masterclass-module-upload-field",
  initialSignedId = null,
  errorMessage = null,
  disabled = false,
}: MasterclassModuleUploadFieldProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const abortRef = useRef<XMLHttpRequest | null>(null)

  const [entry, setEntry] = useState<FileEntry | null>(() => {
    if (initialFilename) {
      return {
        filename: initialFilename,
        byteSize: 0,
        contentType: "",
        status: "ready",
        signedId: initialSignedId,
        progress: 100,
        error: null,
      }
    }
    return null
  })

  const updateStatus = useCallback(
    (status: UploadStatus) => {
      onStatusChange?.(status)
    },
    [onStatusChange],
  )

  const handleFileSelect = useCallback(
    (file: File) => {
      if (file.size > MAX_BYTES) {
        setEntry({
          filename: file.name,
          byteSize: file.size,
          contentType: file.type,
          status: "error",
          signedId: null,
          progress: 0,
          error: t("masterclasses.modules.form.upload.failed"),
        })
        updateStatus("error")
        return
      }

      const baseEntry: FileEntry = {
        filename: file.name,
        byteSize: file.size,
        contentType: file.type,
        status: "uploading",
        signedId: null,
        progress: 0,
        error: null,
      }
      setEntry(baseEntry)
      updateStatus("uploading")

      const upload = new DirectUpload(file, directUploadUrl, {
        directUploadWillStoreFileWithXHR(xhr) {
          abortRef.current = xhr
          xhr.upload.addEventListener("progress", (event) => {
            if (!event.lengthComputable) return
            const progress = Math.round((event.loaded / event.total) * 100)
            setEntry((prev) => (prev ? { ...prev, progress } : prev))
          })
        },
      })

      upload.create((error, blob) => {
        abortRef.current = null

        if (error) {
          setEntry((prev) =>
            prev
              ? {
                  ...prev,
                  status: "error",
                  error: t("masterclasses.modules.form.upload.failed"),
                  progress: 0,
                }
              : prev,
          )
          updateStatus("error")
          return
        }

        setEntry((prev) =>
          prev
            ? {
                ...prev,
                status: "ready",
                signedId: blob.signed_id,
                progress: 100,
                error: null,
              }
            : prev,
        )
        updateStatus("ready")
      })
    },
    [directUploadUrl, t, updateStatus],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) handleFileSelect(file)
      // Reset so re-picking the same filename fires onChange again.
      event.target.value = ""
    },
    [handleFileSelect],
  )

  const handleCancel = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setEntry(null)
    updateStatus("idle")
  }, [updateStatus])

  const handleRemove = useCallback(() => {
    setEntry(null)
    updateStatus("idle")
  }, [updateStatus])

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_MIME}
        onChange={handleInputChange}
        className="sr-only"
        aria-label={t("masterclasses.modules.form.upload.choose_aria")}
        data-testid={`${testId}-input`}
        disabled={disabled}
      />

      {!entry && (
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            data-testid={`${testId}-choose`}
          >
            {t("masterclasses.modules.form.upload.choose")}
          </Button>
          <p className="text-muted-foreground text-xs">
            {t("masterclasses.modules.form.upload.accept_helper")}
          </p>
        </div>
      )}

      {entry && (
        <div
          className={cn(
            "flex flex-col gap-2 rounded-md border p-3 text-sm",
            entry.status === "error"
              ? "border-destructive bg-destructive/5"
              : "border-input",
          )}
          data-testid={`${testId}-entry`}
          data-status={entry.status}
        >
          <div className="flex items-center gap-2">
            {entry.status === "uploading" && (
              <Loader2
                className="text-muted-foreground size-4 shrink-0 animate-spin"
                aria-hidden="true"
              />
            )}
            {entry.status === "ready" && (
              <CheckCircle2
                className="size-4 shrink-0 text-emerald-600"
                aria-hidden="true"
              />
            )}
            <span
              className="flex-1 truncate font-medium"
              data-testid={`${testId}-filename`}
            >
              {entry.filename}
            </span>
            {(entry.status === "uploading" ||
              entry.status === "ready" ||
              entry.status === "error") && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={
                  entry.status === "uploading" ? handleCancel : handleRemove
                }
                aria-label={
                  entry.status === "uploading"
                    ? t("masterclasses.modules.form.upload.cancel")
                    : t("masterclasses.modules.form.upload.remove")
                }
                data-testid={`${testId}-${entry.status === "uploading" ? "cancel" : "remove"}`}
                className="size-7 p-0"
              >
                <X className="size-3.5" aria-hidden="true" />
              </Button>
            )}
          </div>

          {entry.status === "uploading" && (
            <>
              <Progress
                value={entry.progress}
                aria-valuenow={entry.progress}
                role="progressbar"
                data-testid={`${testId}-progress`}
              />
              <p
                className="text-muted-foreground text-xs tabular-nums"
                data-testid={`${testId}-progress-label`}
              >
                {t("masterclasses.modules.form.upload.uploading", {
                  loaded: formatBytes(
                    Math.round((entry.byteSize * entry.progress) / 100),
                  ),
                  total: formatBytes(entry.byteSize),
                  percent: entry.progress,
                })}
              </p>
            </>
          )}

          {entry.status === "ready" && (
            <p
              className="text-xs text-emerald-700"
              data-testid={`${testId}-ready`}
            >
              {t("masterclasses.modules.form.upload.ready")}
            </p>
          )}

          {entry.status === "error" && entry.error && (
            <p
              className="text-destructive text-xs"
              role="alert"
              data-testid={`${testId}-error`}
            >
              {entry.error}
            </p>
          )}

          {entry.status === "ready" && entry.signedId && (
            <input type="hidden" name={fieldName} value={entry.signedId} />
          )}
        </div>
      )}

      {errorMessage && (
        <p
          className="text-destructive text-sm"
          role="alert"
          data-testid={`${testId}-server-error`}
        >
          {errorMessage}
        </p>
      )}
    </div>
  )
}

// Format raw byte count to a short human string ("12.3 MB"). Matches
// the OKS-54 / OKS-29 attachment formatting helpers (no shared util
// yet; consolidate when the third caller lands).
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
